import { last, sortBy } from 'lodash';
import moment from 'moment/moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SWRConfiguration } from 'swr';

import {
  AppraisalTemplate,
  getEmployeeAppraisalTemplates,
} from '~/data/appraisal';
import { TabModules, useModulesConfig } from '~/data/company';
import {
  EmployeePerformanceGoals,
  getEmployeePerformanceGoals,
} from '~/data/performance';
import { PerformanceTabs } from '~/data/tabs';
import { UseSWROutput, useSWR } from '~/hooks/useSWR';
import { TeamPerformanceWidgetProps } from '~/scenes/MyWorkspace/components/Widgets/components/TeamPerformanceWidget/component';
import { getWidgetDataRequestRoles } from '~/scenes/MyWorkspace/components/Widgets/data';
import {
  EvaluationSummaryDto,
  PerformanceGoalDto,
} from '~/services/Http/client.people.generated';
import {
  useGetAppraisalEvaluations,
  useGetTeamPerformanceScores,
} from '~/services/Http/hooks';

export type TeamPerformanceWidgetTableRow = {
  id: number;
  performance: PerformanceGoalDto;
  latestV2Score: number;
  appraisal: {
    name: string;
    status: string;
    lastUpdatedAt: string;
  };
};

export const useTeamPerformanceWidget = (
  config: TeamPerformanceWidgetProps['config'],
) => {
  const { t } = useTranslation();

  const { isModuleEnabled, isModuleTabEnabled } = useModulesConfig();

  const { isTabDisabled, isGoalsV2Enabled, isAppraisalsEnabled } =
    useMemo(() => {
      return {
        isTabDisabled: !isModuleEnabled(TabModules.PERFORMANCE),
        isGoalsV2Enabled: isModuleTabEnabled(
          TabModules.PERFORMANCE,
          PerformanceTabs.GoalEvaluation,
        ),
        isAppraisalsEnabled: isModuleTabEnabled(
          TabModules.PERFORMANCE,
          PerformanceTabs.AppraisalEvaluation,
        ),
      };
    }, [isModuleEnabled, isModuleTabEnabled]);

  const swrConfig: SWRConfiguration = { revalidateOnFocus: false };

  const dataRequestRoles = useMemo(() => {
    return getWidgetDataRequestRoles(config?.role);
  }, [config]);

  const {
    data: performanceScores,
    loading,
    failedToLoad,
  } = useGetTeamPerformanceScores(
    {
      employeeId: 'me',
      roles: dataRequestRoles,
    },
    swrConfig,
  );

  const { performance, employeeIds } = useMemo(() => {
    const performance = sortBy(performanceScores, (performance) => [
      performance.employee.lastName,
      performance.employee.firstName,
    ]);

    return {
      performance,
      employeeIds: performance.reduce((employeeIds, performanceGoal) => {
        const employeeId = performanceGoal?.employee?.id;

        if (employeeId && !employeeIds.includes(employeeId)) {
          return [...employeeIds, employeeId];
        }

        return employeeIds;
      }, [] as number[]),
    };
  }, [performanceScores]);

  const {
    appraisalEvaluations,
    loading: loadingAppraisals,
    failedToLoad: failedToLoadAppraisals,
  } = useGetAppraisalEvaluations(
    {
      isEnabled: isAppraisalsEnabled,
    },
    swrConfig,
  );

  const {
    data: goalsV2AndAppraisalTemplates,
    isLoading: isLoadingGoalsV2AndAppraisalTemplates,
    failedToLoad: failedToLoadGoalsV2AndAppraisalTemplates,
  }: UseSWROutput<
    {
      employeeId: number;
      performanceGoalsV2: EmployeePerformanceGoals[];
      appraisalTemplates: AppraisalTemplate[];
    }[]
  > = useSWR(
    (isGoalsV2Enabled || isAppraisalsEnabled) && employeeIds.length > 0
      ? ['useTeamPerformanceWidget', employeeIds]
      : null,
    ([_key, employeeIds]: [string, number[]]) => {
      return Promise.all(
        employeeIds.map((employeeId) =>
          Promise.all([
            isGoalsV2Enabled
              ? getEmployeePerformanceGoals(employeeId)
              : Promise.resolve([]),
            isAppraisalsEnabled
              ? getEmployeeAppraisalTemplates(employeeId)
              : Promise.resolve([]),
          ]).then(([performanceGoalsV2, appraisalTemplates]) => {
            return {
              employeeId,
              performanceGoalsV2,
              appraisalTemplates,
            };
          }),
        ),
      );
    },
    swrConfig,
  );

  const resolveLastAppraisalEvaluation = useCallback(
    (employeeId: number, appraisalTemplates: AppraisalTemplate[]) => {
      const resolveActiveAppraisalTemplates = (
        appraisalTemplates: AppraisalTemplate[],
      ) => {
        const activeStateTemplates = appraisalTemplates.filter(
          ({ status }) => status === 'ACTIVE',
        );

        const nonDeadlinedTemplates = activeStateTemplates.filter(
          ({ deadline }) => deadline > new Date().toISOString(),
        );

        return nonDeadlinedTemplates.length === 0
          ? activeStateTemplates
          : nonDeadlinedTemplates;
      };

      const resolveAppraisalEvaluations = (
        appraisalTemplates: AppraisalTemplate[],
        appraisalEvaluations: EvaluationSummaryDto[],
      ): {
        template: AppraisalTemplate | undefined;
        evaluation: EvaluationSummaryDto | undefined;
      } => {
        // No Templates
        if (appraisalTemplates.length === 0) {
          return {
            template: undefined,
            evaluation: undefined,
          };
        }

        const activeTemplateIds = appraisalTemplates.map(
          (template: { id: string }) => template.id,
        );
        const matchingEvaluations = appraisalEvaluations?.filter(
          (evaluation: EvaluationSummaryDto) =>
            evaluation.employee.id === employeeId &&
            activeTemplateIds.includes(evaluation.templateId),
        );

        // Has Evaluations
        if (matchingEvaluations.length > 0) {
          const lastEvaluation = last(
            sortBy(matchingEvaluations, (e) => e.lastUpdatedAt),
          );

          const template = appraisalTemplates.find(
            (t) => t.id === lastEvaluation?.templateId,
          );

          return {
            template,
            evaluation: lastEvaluation,
          };
        }

        // No Evaluations
        return {
          template: last(sortBy(appraisalTemplates, (t) => t.updatedTime)),
          evaluation: undefined,
        };
      };

      return resolveAppraisalEvaluations(
        resolveActiveAppraisalTemplates(appraisalTemplates),
        appraisalEvaluations,
      );
    },
    [appraisalEvaluations],
  );

  const statusTranslation = useCallback(
    (status: string) => {
      switch (status) {
        case 'NOT_STARTED':
          return t('notStarted');
        case 'IN_PROGRESS':
          return t('inProgress');
        case 'COMPLETED':
          return t('completed');
        case 'EMPLOYEE_APPROVED':
          return t('laEmployeeApproved');
        default:
          return status;
      }
    },
    [t],
  );

  const teamPerformanceWidgetTableRows = useMemo(() => {
    return performance.map(
      (performanceGoals): TeamPerformanceWidgetTableRow => {
        const employeeId = performanceGoals?.employee?.id;

        const { performanceGoalsV2, appraisalTemplates } =
          goalsV2AndAppraisalTemplates?.find(
            (data) => data.employeeId === employeeId,
          ) ?? {
            employeeId,
            performanceGoalsV2: [],
            appraisalTemplates: [],
          };

        const lastGoalsV2 = last(performanceGoalsV2);

        const { template, evaluation } = resolveLastAppraisalEvaluation(
          employeeId,
          appraisalTemplates,
        );

        return {
          id: employeeId,
          performance: performanceGoals,
          latestV2Score: lastGoalsV2?.totScore ?? 0,
          appraisal: {
            name: template?.subject ?? '',
            status: statusTranslation(evaluation?.status ?? 'NOT_STARTED'),
            lastUpdatedAt:
              !!evaluation?.status && !!evaluation?.lastUpdatedAt
                ? moment(evaluation.lastUpdatedAt).format('L LT')
                : '',
          },
        };
      },
    );
  }, [
    performance,
    goalsV2AndAppraisalTemplates,
    resolveLastAppraisalEvaluation,
    statusTranslation,
  ]);

  return {
    tableRows: teamPerformanceWidgetTableRows,
    loading:
      loading || loadingAppraisals || isLoadingGoalsV2AndAppraisalTemplates,
    failedToLoad:
      failedToLoad ||
      failedToLoadAppraisals ||
      failedToLoadGoalsV2AndAppraisalTemplates,
    isTabDisabled,
  };
};
